import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Loader2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1620203853151-496c7228306c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={HERO} alt="Industrial piping" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-[#7A1216]/55 to-black/70" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="text-3xl font-bold text-white tracking-tight" style={{ fontFamily: "Outfit" }}>
            Analytics for Dee Piping
          </h2>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm fade-up">
          <div className="flex items-center gap-3 mb-10">
            <img src="/dee-logo-red.png" alt="Dee Piping" className="h-10 w-auto object-contain" />
            <div>
              <div className="font-extrabold text-lg text-slate-900 tracking-tight" style={{ fontFamily: "Outfit" }}>DEE PIPING</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">SAP Agent Insights</div>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-1">Sign in</h1>
          <p className="text-sm text-slate-500 mb-7">Access your SAP financial insights workspace</p>
          {error && (
            <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3" data-testid="login-error">{error}</div>
          )}
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1.5 w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-[#B01E24]/60"
                placeholder="you@deepiping.com"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password</label>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1.5 w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-[#B01E24]/60"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit" disabled={loading}
              data-testid="login-submit-button"
              className="w-full flex items-center justify-center gap-2 bg-[#B01E24] hover:bg-[#9A171D] disabled:opacity-60 text-white font-medium rounded-lg py-2.5 text-sm transition-colors"
            >
              {loading && <Loader2 size={15} className="animate-spin" />} Sign In
            </button>
          </form>
          <p className="mt-6 text-sm text-slate-500 text-center">
            New here?{" "}
            <Link to="/register" data-testid="register-now-link" className="text-[#B01E24] hover:text-[#9A171D] font-medium">
              Register now
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
